/**
 * Public page (no auth) — accept a user invite token and set the user's
 * password. The link is generated when an admin creates a user via "email
 * invite link" and is copy-pasted to the recipient.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../contexts/AuthContext';

export default function AcceptInvite() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const token = params.get('token') || '';
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!token) { setError('Missing invite token in URL.'); return; }
        api.get(`/invites/${token}`)
            .then(r => setInfo(r.data))
            .catch(e => setError(formatApiError(e?.response?.data?.detail) || 'Invalid invite'));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error('Password must be at least 8 characters.');
        if (password !== confirm) return toast.error('Passwords do not match.');
        setSaving(true);
        try {
            await api.post('/invites/accept', { token, password });
            // Use AuthContext.login so user state is hydrated before navigation
            const r = await login(info.email, password);
            if (r.ok) {
                toast.success('Welcome aboard!');
                navigate('/dashboard');
            } else {
                toast.error(r.error || 'Sign-in failed — please log in manually.');
                navigate('/login');
            }
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || 'Could not accept invite');
        } finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--bm-bg)] p-6">
            <div className="w-full max-w-md bg-white border border-[color:var(--bm-border)] rounded-xl shadow-sm p-8" data-testid="accept-invite-page">
                <div className="mb-6 flex justify-center">
                    <BrandLogo />
                </div>

                {error && (
                    <div className="text-center space-y-3" data-testid="invite-error">
                        <div className="text-red-700 text-[14px] font-semibold">{error}</div>
                        <Link to="/login" className="text-[12px] underline text-[color:var(--bm-text-muted)]">Back to login →</Link>
                    </div>
                )}

                {!error && !info && <div className="text-center text-[color:var(--bm-text-muted)] text-[13px]">Loading invite…</div>}

                {!error && info && (
                    <>
                        <div className="text-center mb-5">
                            <h1 className="font-display text-[20px] font-semibold">Welcome, {info.name}</h1>
                            <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1">
                                Set a password for <span className="font-medium">{info.email}</span> to activate your Beyond Measure HQ account.
                            </div>
                        </div>
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <Label className="text-[12px] font-medium">New password</Label>
                                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1 h-10" autoFocus placeholder="Min 8 characters" data-testid="invite-password-input" />
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium">Confirm password</Label>
                                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1 h-10" data-testid="invite-password-confirm" />
                            </div>
                            <Button type="submit" disabled={saving} className="w-full h-10" style={{ background: 'var(--bm-primary)' }} data-testid="invite-submit-btn">
                                {saving ? 'Setting up…' : 'Activate account'}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
